/* screens/workspace/feed/usePendingChange.js — общая state-машина pending-правки.
   Извлечена из ConfirmActionCard, чтобы канвас изменений (ChangeCanvas) и компакт-
   карточка управляли ОДНОЙ правкой одинаково: idle → committing → committed →
   (restoring → restored) | cancelled | stale | error. Финальные статусы пишутся
   мутацией pc.status (metadata в памяти) — ремоунт/смена поверхности (канвас ↔
   карточка) видит уже применённую правку и не предлагает «Подтвердить» снова. */

import React from 'react';
import { useChat } from '../../../app/providers/ChatProvider.jsx';
import { useToast } from '../../../app/providers/ToastProvider.jsx';

  const { useState, useEffect, useRef } = React;

  const KIND_LABEL = { ttk: 'ТТК', card: 'Сырьё', menu: 'Меню' };
  const KIND_HREF = (pc) => pc.kind === 'card' ? `/kitchen?tab=ingredients&edit=card-${pc.target_id}`
    : pc.kind === 'menu' ? `/kitchen?tab=menus&edit=menu-${pc.target_id}`
      : `/kitchen?tab=recipes&edit=ttk-${pc.target_id}`;

  // Финальные статусы persist-им в pc.status; транзитные — только локально.
  const PERSISTED = ['committed', 'cancelled', 'restored', 'stale'];

  function usePendingChange(pc) {
    const toast = useToast();
    const chat = useChat ? useChat() : null;
    const [status, setStatus] = useState(pc.status === 'pending' ? 'idle' : (pc.status || 'idle'));
    const [restoreRef, setRestoreRef] = useState(null); // {kind, id, number} — для отката

    // Внешняя смена статуса (вытеснение новой правкой той же цели: провайдер
    // помечает прежний pc 'stale' в onConfirmAction) — подхватываем в уже
    // смонтированной поверхности. Транзитные локальные состояния не трогаем.
    const statusRef = useRef(status);
    statusRef.current = status;
    useEffect(() => {
      const ext = pc.status === 'pending' ? null : pc.status;
      const local = statusRef.current;
      const busyNow = local === 'committing' || local === 'restoring';
      if (ext && ext !== local && !busyNow && PERSISTED.includes(ext)) setStatus(ext);
    }, [pc, pc.status]);

    const goStatus = (s) => { setStatus(s); if (PERSISTED.includes(s)) pc.status = s; };

    // commit → 200 {committed, restore_version_number, target} | 409 stale/expired/gone.
    const commit = () => {
      if (!chat || !chat.commitPendingChange) return;
      setStatus('committing');
      chat.commitPendingChange(pc.id)
        .then((res) => {
          if (res && res.restore_version_number != null) {
            setRestoreRef({ kind: (res.target && res.target.kind) || pc.kind, id: (res.target && res.target.id) || pc.target_id, number: res.restore_version_number });
          }
          goStatus('committed'); toast.show('Правка применена', 'success');
        })
        .catch((err) => {
          if (err && (err.error === 'stale' || err.error === 'expired' || err.error === 'gone' || err.status === 409)) goStatus('stale');
          else setStatus('error');
        });
    };
    // cancel → POST cancel/ (идемпотентно); локально сворачиваем сразу, ошибку игнорим.
    const cancel = () => { goStatus('cancelled'); if (chat && chat.cancelPendingChange) chat.cancelPendingChange(pc.id).catch(() => {}); };
    const restore = () => {
      if (!chat || !chat.restorePendingChange || !restoreRef) { setStatus('committed'); return; }
      setStatus('restoring');
      chat.restorePendingChange(restoreRef.kind, restoreRef.id, restoreRef.number)
        .then(() => { goStatus('restored'); toast.show('Изменение откатано', 'muted'); })
        .catch(() => setStatus('committed'));
    };
    const restoreIdle = () => { setStatus('idle'); pc.status = 'idle'; };

    const busy = status === 'committing' || status === 'restoring';
    const applied = status === 'committed' || status === 'restored';
    return { status, busy, applied, restoreRef, commit, cancel, restore, restoreIdle };
  }

export { usePendingChange, KIND_LABEL, KIND_HREF };
